import type { IIngredientDetailsFormData } from "@/context/data.types";
import { isProduceRacVendorAssessmentLayout } from "@/utils/productSpecificationTypeUtils";

/** Ingredient fields hidden for Produce RAC layouts (datapoint: multi-pack block). */
export const PRODUCE_RAC_HIDDEN_INGREDIENT_FIELDS: ReadonlySet<
  keyof IIngredientDetailsFormData | "productName"
> = new Set(["isMultiPackProduct", "howManyProductsInPack", "productName"]);

/**
 * Multi-pack UI is hidden for Produce RAC layouts.
 * Vendor Name, NFP, and single Ingredient List remain visible.
 */
export function shouldShowIngredientMultipackFields(
  productSpecificationType?: string | null
): boolean {
  return !isProduceRacVendorAssessmentLayout(productSpecificationType);
}

export function shouldShowIngredientDetailsField(
  field: keyof IIngredientDetailsFormData | "productName",
  productSpecificationType?: string | null
): boolean {
  if (!isProduceRacVendorAssessmentLayout(productSpecificationType)) {
    return true;
  }

  return !PRODUCE_RAC_HIDDEN_INGREDIENT_FIELDS.has(field);
}

/**
 * Produce RAC (TJ Label & Branded) vendor assessments do not support multipack.
 * Backend expects `multipack: null` on save/share payloads for those spec types.
 */
export function sanitizeMultipackSavePayloadForVendorAssessment<
  T extends Record<string, unknown>,
>(
  multipack: T | null | undefined,
  productSpecificationType?: string | null
): T | null {
  if (!shouldShowIngredientMultipackFields(productSpecificationType)) {
    return null;
  }

  if (multipack == null || typeof multipack !== "object" || Array.isArray(multipack)) {
    return {} as T;
  }

  return multipack as T;
}
